import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiCallOptions } from '@modules/common/models/api-call-options';
import { ApiCallUrlService } from '@modules/common/services/api-call-url.service';
import { AuthenticationTokenService } from '@modules/common/services/authentication-token.service';
import { HttpErrorService } from '@modules/common/services/http-error.service';
import { IsLoadingService } from '@modules/common/services/is-loading.service';
import { RedirectToLoginService } from '@modules/common/services/redirect-to-login.service';
import { Observable, Subject } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

/**
 * Service used to make calls into the Web API. This service
 * should be used for all API calls, NEVER use Angular's
 * Http or HttpClient directly.
 */
@Injectable({
    providedIn: 'root'
})
export class ApiCallService {
    constructor(
        private httpClient: HttpClient,
        private _apiCallUrlService: ApiCallUrlService,
        private _authenticationTokenService: AuthenticationTokenService,
        private _redirectToLoginService: RedirectToLoginService,
        private _httpErrorService: HttpErrorService,
        private _isLoadingService: IsLoadingService
    ) {
    }

    /**
     * Make an API call to the given API url w/ the given request body. This
     * function returns an observable that provides the result data of the API call.
     * @param options The url, body, and other options used to make the call.
     */
    public callApi<T>(options: ApiCallOptions): Observable<T> {
        this.validate(options);
        options = this.getOptionsWithDefaultValues(options);

        const httpHeaders: { [key: string]: string } = {};

        if (!(options.body instanceof FormData)) {
            httpHeaders['Content-Type'] = 'application/json';
        }

        if (options.sendCredentials) {
            let authTokenObservable = this._authenticationTokenService.getAuthenticationToken();
            const subject = new Subject<T>();
            if (authTokenObservable) {
                if (options.useLoadingIndicator) {
                    this._isLoadingService.setLoading();
                    authTokenObservable = authTokenObservable.pipe(finalize(() => this._isLoadingService.clearLoading()));
                }

                authTokenObservable.pipe(tap(() => { }, x => {
                    subject.error(x);
                })).subscribe(token => {
                    if (token) {
                        httpHeaders['Authorization'] = 'Bearer ' + token;
                        this.post<T>(options, httpHeaders).pipe(
                            tap(() => { }, x => {
                                this._httpErrorService.handleHttpError(x);
                                subject.error(x);
                            })).subscribe(result =>
                                subject.next(result)
                            );
                    } else {
                        this._redirectToLoginService.redirectToLogin();
                        subject.complete();
                    }
                    });

                return subject;
            }

            this._redirectToLoginService.redirectToLogin();
            subject.complete();
            return subject;
        }

        return this.post<T>(options, httpHeaders).pipe(
            tap<T>(() => { }, x => {
                this._httpErrorService.handleHttpError(x);
            }));
    }

    private post<T>(options: ApiCallOptions, httpHeaders: { [key: string]: string }): Observable<T> {
        let result = this.httpClient.post<T>(
            this._apiCallUrlService.getUrl(options.url, options.includeEnvironmentAndCase),
            options.body,
            { headers: new HttpHeaders(httpHeaders) }
        );

        if (options.useLoadingIndicator) {
            this._isLoadingService.setLoading();
            result = result.pipe(finalize(() => this._isLoadingService.clearLoading()));
        }

        return result;
    }

    /**
     * Return a copy of the options object w/ default values 
     * @param options
     */
    private getOptionsWithDefaultValues(options: ApiCallOptions): ApiCallOptions {
        options = Object.assign({}, options);
        if (typeof options.sendCredentials !== "boolean") {
            options.sendCredentials = true;
        }

        if (typeof options.useLoadingIndicator !== "boolean") {
            options.useLoadingIndicator = true;
        }

        return options;
    }

    private validate(options: ApiCallOptions): void {
        if (!options) {
            throw new Error("ApiCallOptions are required");
        }

        if (!options.url) {
            throw new Error("ApiCallOptions.url is required");
        }
    }
}
